/* eslint-disable @typescript-eslint/no-explicit-any */
import { logger } from './logger';
import axios from './axios';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

// CRITICAL: Helper to check if we're on billing page
// Check both pathname and full path to handle different URL formats
function isBillingPage(): boolean {
    if (typeof window === 'undefined') return false;
    const pathname = window.location.pathname;
    const fullPath = window.location.pathname + window.location.search;
    // Check if pathname matches or if URL contains billing/choose-plan
    return pathname === '/billing/choose-plan' || 
           pathname.includes('/billing/choose-plan') ||
           fullPath.includes('/billing/choose-plan');
}

// CRITICAL: Helper to check if this is a billing-related API call (allow it)
function isBillingApiCall(path: string): boolean {
    return path.includes('/billing/');
}

export function authHeader(): HeadersInit {
    const localToken = localStorage.getItem('token');
    const sessionToken = sessionStorage.getItem('token');
    const token = (localToken || sessionToken || '').trim();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

function mapApiError(method: string, path: string, error: any, responseTime: number): Error {
    const status = error?.response?.status;
    const data = error?.response?.data;
    const mappedError = new Error(`${method} ${path} failed: ${status ?? 'unknown'}`);
    (mappedError as any).status = status;
    (mappedError as any).data = data;
    (mappedError as any).responseTime = responseTime;
    return mappedError;
}

export async function apiGet<T>(path: string, params?: Record<string, string>): Promise<T> {
    const startTime = performance.now();
    const fullUrl = `${API_BASE_URL}${path}`;
    
    // CRITICAL: Block all API calls when on billing page (except billing API itself)
    if (isBillingPage() && !isBillingApiCall(path)) {
        logger.warn('API', `GET ${path} blocked: on billing page`);
        return Promise.reject(new Error('API call blocked: on billing page'));
    }
    
    logger.api('GET', fullUrl, params);
    
    try {
        const res = await axios.get<T>(path, {
            params,
            headers: {
                ...authHeader(),
            },
        });
        const responseTime = Math.round(performance.now() - startTime);
        const data = res.data as T;
        logger.api('GET', fullUrl, params, {
            status: res.status,
            statusText: res.statusText,
            data,
            responseTime,
        });
        
        return data;
    } catch (error: any) {
        const responseTime = Math.round(performance.now() - startTime);
        const status = error?.response?.status;
        const statusText = error?.response?.statusText;
        const data = error?.response?.data;
        logger.api('GET', fullUrl, params, null, {
            status,
            statusText,
            data,
            message: error?.message,
            responseTime,
        });
        throw mapApiError('GET', path, error, responseTime);
    }
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
    const startTime = performance.now();
    const fullUrl = `${API_BASE_URL}${path}`;
    
    // CRITICAL: Block all API calls when on billing page (except billing API itself)
    if (isBillingPage() && !isBillingApiCall(path)) {
        logger.warn('API', `POST ${path} blocked: on billing page`);
        return Promise.reject(new Error('API call blocked: on billing page'));
    }
    
    logger.api('POST', fullUrl, body);
    
    try {
        const res = await axios.post<T>(path, body, {
            headers: {
                ...authHeader(),
            },
        });
        const responseTime = Math.round(performance.now() - startTime);
        const data = res.data as T;
        logger.api('POST', fullUrl, body, {
            status: res.status,
            statusText: res.statusText,
            data,
            responseTime,
        });
        
        return data;
    } catch (error: any) {
        const responseTime = Math.round(performance.now() - startTime);
        const status = error?.response?.status;
        const statusText = error?.response?.statusText;
        const data = error?.response?.data;
        logger.api('POST', fullUrl, body, null, {
            status,
            statusText,
            data,
            message: error?.message,
            responseTime,
        });
        throw mapApiError('POST', path, error, responseTime);
    }
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
    const startTime = performance.now();
    const fullUrl = `${API_BASE_URL}${path}`;
    
    // CRITICAL: Block all API calls when on billing page (except billing API itself)
    if (isBillingPage() && !isBillingApiCall(path)) {
        logger.warn('API', `PUT ${path} blocked: on billing page`);
        return Promise.reject(new Error('API call blocked: on billing page'));
    }
    
    logger.api('PUT', fullUrl, body);
    
    try {
        const res = await axios.put<T>(path, body, {
            headers: {
                ...authHeader(),
            },
        });
        const responseTime = Math.round(performance.now() - startTime);
        const data = res.data as T;
        logger.api('PUT', fullUrl, body, {
            status: res.status,
            statusText: res.statusText,
            data,
            responseTime,
        });
        
        return data;
    } catch (error: any) {
        const responseTime = Math.round(performance.now() - startTime);
        const status = error?.response?.status;
        const statusText = error?.response?.statusText;
        const data = error?.response?.data;
        logger.api('PUT', fullUrl, body, null, {
            status,
            statusText,
            data,
            message: error?.message,
            responseTime,
        });
        throw mapApiError('PUT', path, error, responseTime);
    }
}

export async function apiDelete<T>(path: string, params?: Record<string, string>): Promise<T> {
    const startTime = performance.now();
    const fullUrl = `${API_BASE_URL}${path}`;
    
    // CRITICAL: Block all API calls when on billing page (except billing API itself)
    if (isBillingPage() && !isBillingApiCall(path)) {
        logger.warn('API', `DELETE ${path} blocked: on billing page`);
        return Promise.reject(new Error('API call blocked: on billing page'));
    }
    
    logger.api('DELETE', fullUrl, params);
    
    try {
        const res = await axios.delete<T>(path, {
            params,
            headers: {
                ...authHeader(),
            },
        });
        const responseTime = Math.round(performance.now() - startTime);
        const data = res.data as T;
        logger.api('DELETE', fullUrl, params, {
            status: res.status,
            statusText: res.statusText,
            data,
            responseTime,
        });
        
        return data;
    } catch (error: any) {
        const responseTime = Math.round(performance.now() - startTime);
        const status = error?.response?.status;
        const statusText = error?.response?.statusText;
        const data = error?.response?.data;

        logger.api('DELETE', fullUrl, params, null, {
            status,
            statusText,
            data,
            message: error?.message,
            responseTime,
        });
        throw mapApiError('DELETE', path, error, responseTime);
    }
}
